import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { quotesAPI, productsAPI, leadsAPI, organizationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  TrashIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../components/UI/Modal';

const Quotes = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    title: '',
    lead: '',
    customerOrganization: '',
    customer: { name: '', email: '', phone: '' },
    items: [],
    validUntil: '',
    terms: '',
    notes: '',
  });

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: async () => { const r = await productsAPI.getAll(); return r.data.data; },
  });

  const { data: leadsData } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => { const r = await leadsAPI.getAll(); return r.data.data; },
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => { const r = await organizationsAPI.getAll(); return r.data.data; },
  });

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['quotes', searchTerm],
    queryFn: async () => {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const response = await quotesAPI.getAll(params);
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: quotesAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['quotes']);
      toast.success('Quote created successfully');
      closeModal();
    },
    onError: (error) => {
      const msg = error.response?.data?.message || 'Failed to create quote';
      const errs = error.response?.data?.errors;
      if (errs) errs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      else toast.error(msg);
    },
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      title: '',
      lead: '',
      customerOrganization: '',
      customer: { name: '', email: '', phone: '' },
      items: [],
      validUntil: '',
      terms: '',
      notes: '',
    });
  };

  const handleLeadChange = (leadId) => {
    const lead = leadsData?.find(l => l._id === leadId);
    if (lead) {
      setFormData({
        ...formData,
        lead: leadId,
        customerOrganization: lead.customerOrganization?._id || lead.customerOrganization || '',
        customer: {
          name: `${lead.firstName} ${lead.lastName}`,
          email: lead.email,
          phone: lead.phone || '',
        },
      });
    }
  };

  const handleOrgChange = (orgId) => {
    const org = organizations?.find(o => o._id === orgId);
    if (org) {
      setFormData({
        ...formData,
        customerOrganization: orgId,
        customer: {
          ...formData.customer,
          name: formData.customer.name || org.name,
        },
      });
    } else {
      setFormData({ ...formData, customerOrganization: orgId });
    }
  };

  const addProductItem = (productId) => {
    const product = productsData?.find(p => p._id === productId);
    if (product) {
      setFormData({
        ...formData,
        items: [...formData.items, {
          product: product._id,
          productName: product.name,
          description: product.description || '',
          quantity: 1,
          unitPrice: product.price,
          discount: 0,
          tax: 0,
        }],
      });
    }
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const items = [...formData.items];
    items[index][field] = ['quantity', 'unitPrice', 'discount', 'tax'].includes(field)
      ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items });
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const afterDiscount = subtotal - (subtotal * (item.discount / 100));
    return (afterDiscount + (afterDiscount * (item.tax / 100))).toFixed(2);
  };

  const calculateQuoteTotal = () =>
    formData.items.reduce((sum, item) => sum + parseFloat(calculateItemTotal(item)), 0).toFixed(2);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Please add at least one product item');
      return;
    }
    const payload = {
      title: formData.title,
      customerOrganization: formData.customerOrganization,
      customer: formData.customer,
      items: formData.items,
      validUntil: formData.validUntil,
      terms: formData.terms,
      notes: formData.notes,
    };
    if (formData.lead) payload.lead = formData.lead;
    createMutation.mutate(payload);
  };

  const downloadPDF = async (quoteId) => {
    try {
      const response = await quotesAPI.downloadPDF(quoteId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const sendEmail = async (quoteId) => {
    try {
      await quotesAPI.sendEmail(quoteId);
      toast.success('Quote email sent');
    } catch { toast.error('Failed to send email'); }
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="h-8 w-32 bg-gray-200 dark:bg-surface-700 rounded-lg animate-pulse" />
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Quotes</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Manage and send quotes to customers</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Quote
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="glass-input pl-10"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Quote</th>
                <th>Customer</th>
                <th>Organization</th>
                <th>Amount</th>
                <th>Valid Until</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes?.map((quote) => {
                return (
                  <tr key={quote._id}>
                    <td>
                      <Link to={`/quotes/${quote._id}`} className="text-primary-600 hover:text-primary-700 font-semibold">
                        #{quote.quoteNumber}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{quote.title}</p>
                    </td>
                    <td>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{quote.customer?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{quote.customer?.email}</p>
                    </td>
                    <td>
                      {quote.customerOrganization ? (
                        <div className="flex items-center gap-1.5">
                          <BuildingOfficeIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{quote.customerOrganization.name || quote.customerOrganization}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="text-sm font-bold text-emerald-600">${quote.total?.toLocaleString()}</td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : '-'}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadPDF(quote._id)} className="btn btn-ghost p-1.5" title="Download PDF">
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => sendEmail(quote._id)} className="btn btn-ghost p-1.5 text-blue-600" title="Send Email">
                          <EnvelopeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!quotes?.length && (
            <div className="text-center py-16">
              <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No quotes found</p>
              <button onClick={() => setIsModalOpen(true)} className="btn btn-primary mt-4 text-sm">
                Create your first quote
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Quote Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title="Create Quote" size="xl">
        <form onSubmit={handleSubmit} className="mt-4 space-y-5">
          {/* Basic Info */}
          <div>
            <label className="label">Quote Title *</label>
            <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="glass-input" placeholder="e.g., Website Development Quote" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Link to Lead (Optional)</label>
              <select value={formData.lead} onChange={(e) => handleLeadChange(e.target.value)} className="glass-input">
                <option value="">Select a lead...</option>
                {leadsData?.map((lead) => (
                  <option key={lead._id} value={lead._id}>{lead.firstName} {lead.lastName} - {lead.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Organization *</label>
              <select value={formData.customerOrganization} onChange={(e) => handleOrgChange(e.target.value)} className="glass-input" required>
                <option value="">Select organization</option>
                {organizations?.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          {/* Customer Info */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Contact Person</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Name *</label>
                <input type="text" required value={formData.customer.name} onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, name: e.target.value } })} className="glass-input" />
              </div>
              <div>
                <label className="label">Email *</label>
                <input type="email" required value={formData.customer.email} onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, email: e.target.value } })} className="glass-input" />
              </div>
              <div>
                <label className="label">Phone</label>
                <input type="tel" value={formData.customer.phone} onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, phone: e.target.value } })} className="glass-input" />
              </div>
            </div>
          </div>

          {/* Quote Items */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Quote Items *</h3>
              <select
                onChange={(e) => { if (e.target.value) { addProductItem(e.target.value); e.target.value = ''; } }}
                className="glass-input w-auto text-sm"
              >
                <option value="">+ Add Product</option>
                {productsData?.filter(p => p.status === 'active').map((p) => (
                  <option key={p._id} value={p._id}>{p.name} - ${p.price}</option>
                ))}
              </select>
            </div>

            {formData.items.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-surface-900 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-400">No items added. Select products from the dropdown above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 dark:bg-surface-900 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{item.productName}</h4>
                        {item.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>}
                      </div>
                      <button type="button" onClick={() => removeItem(index)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-5 gap-3">
                      <div>
                        <label className="label text-xs">Quantity</label>
                        <input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="glass-input text-sm" />
                      </div>
                      <div>
                        <label className="label text-xs">Unit Price</label>
                        <input type="number" step="0.01" value={item.unitPrice} onChange={(e) => updateItem(index, 'unitPrice', e.target.value)} className="glass-input text-sm" />
                      </div>
                      <div>
                        <label className="label text-xs">Discount %</label>
                        <input type="number" min="0" max="100" step="0.01" value={item.discount} onChange={(e) => updateItem(index, 'discount', e.target.value)} className="glass-input text-sm" />
                      </div>
                      <div>
                        <label className="label text-xs">Tax %</label>
                        <input type="number" min="0" step="0.01" value={item.tax} onChange={(e) => updateItem(index, 'tax', e.target.value)} className="glass-input text-sm" />
                      </div>
                      <div>
                        <label className="label text-xs">Total</label>
                        <div className="mt-1 px-3 py-2 bg-gray-100 dark:bg-surface-700 rounded-lg text-sm font-bold text-emerald-600">
                          ${calculateItemTotal(item)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <div className="text-right bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-4 py-2.5 border border-emerald-200 dark:border-emerald-800">
                    <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Total:</span>
                    <span className="text-lg font-bold text-emerald-600">${calculateQuoteTotal()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="label">Valid Until *</label>
              <input type="date" required value={formData.validUntil} onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })} min={new Date().toISOString().split('T')[0]} className="glass-input" />
            </div>
            <div>
              <label className="label">Terms & Conditions</label>
              <textarea value={formData.terms} onChange={(e) => setFormData({ ...formData, terms: e.target.value })} rows={2} className="glass-input" />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="glass-input" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create Quote'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Quotes;





